import _ from 'lodash';
import ERRORS from '../errors/errorids';
import BaseError from '../errors/baseerror';
import HttpError from '../errors/httperror';
import knownProperties from '../errors/knownproperties';
import { getErrorsById } from '../errors/errorhelper';
import { loggerConfig, REACT_APP_BUILD_NUMBER, REACT_APP_BRANCH, REACT_APP_BUILD_VCS_NUMBER } from '../config';
import clientId from '../utils/clientid';
import axios from 'axios';

const ERROR_HISTORY_STORAGE_KEY = loggerConfig.errorHistoryStorageKey;
const ERROR_QUEUE_STORAGE_KEY = loggerConfig.errorQueueStorageKey;

const MAX_TIME_DIFF_BETWEEN_ERRORS = 5000;
const ERROR_QUEUE_MAX_RECORDS = 1000;
const LOGGER_AJAX_RETRY_MAX = 5;
const LOGGER_AJAX_RETRY_TIMEOUT = 7000;

const errorsById = getErrorsById();

const userInfo = {};
let lastAuthorizedUserEmail;
let browserId;
let browserTabId;

initBrowserDetails();

function initUserInfo(userData, companyId, isPayingCompany) {
	if (_.isEmpty(userData)) return;
	userInfo.id = userData.id;
	userInfo.name = userData.firstName && userData.lastName
		?
		`${userData.firstName} ${userData.lastName}`
		:
		userData.name;
	userInfo.mail = userData.mainEmail;
	userInfo.isSignedIn = true;

	userInfo.relatedCompanyId = companyId;
	userInfo.isPayingCompany = { payingSelectedCompany: Boolean(isPayingCompany) };
	window.localStorage.setItem('camonlineLastAuthorizedUserEmail', userData.mainEmail);
}

let isSavedBeingSentStateCleared = false;
function clearSavedIsBeingSentState() {
	// Clear saved isBeingSent state which might be saved before refresh
	if (!isSavedBeingSentStateCleared) {
		flagItemInErrorQueuBeingSentStatus('all', false);
		isSavedBeingSentStateCleared = true;
	}
}

function parseMessage(logMessage) {
	const parsedLogMessage = {};

	Object.keys(logMessage).forEach(function (i) {
		if (typeof knownProperties[i] === 'function') {
			if (!knownProperties[i](parsedLogMessage, i, logMessage[i])) {
				knownProperties.miscellaneous(parsedLogMessage, i, logMessage[i]);
			}
		} else {
			knownProperties.miscellaneous(parsedLogMessage, i,logMessage[i]);
		}
	});

	return parsedLogMessage;
}

// [P9052-15244] disable items field due to it causing logs to not be
// stored in kibana. We will be re-adding this after review so do not delete it yet.
function sendToServer(
	objectToLog,
	forceSilentMessageType /*includeItems*/,
	pastTimestamp,
	pastBrowserId,
	pastBrowserTabId,
	pastLastAuthorizedUserEmail) {

	if (!objectToLog) {
		objectToLog = { message: 'No message' };
	}

	// string passed
	if ('' + objectToLog === objectToLog) {
		objectToLog = { message: objectToLog };
	}

	if (objectToLog instanceof ErrorEvent) {
		// In case someone tries to log ErrorEvent directly
		// without using custom class (AppError etc)
		// then use ErrorEvent.error as object to log
		// https://developer.mozilla.org/en-US/docs/Web/API/ErrorEvent
		objectToLog = objectToLog.error;
	}

	// Always copy props to new object, otherwise props (user, meta etc) added in logger.js 
	// to error object will be serialized in baseError.getErrorHistoryToLog()
	const packetToLog = objectToLog instanceof Error ||
		(objectToLog instanceof Event && objectToLog.type && objectToLog.type === 'error') ||
		pastTimestamp ? {} : objectToLog;

	// save App data
	packetToLog.name = this.componentName;
	packetToLog.meta = {
		browserId: pastBrowserId || browserId,
		browserTabId: pastBrowserTabId || browserTabId,
		timestamp: pastTimestamp || Date.now(),
		lastAuthorizedUserEmail: pastLastAuthorizedUserEmail || lastAuthorizedUserEmail,
		url: window.location.href,
		app: {
			commit: REACT_APP_BUILD_VCS_NUMBER,
			branch: REACT_APP_BRANCH
		},
		// "online" does not always mean connection to the internet,
		// it can also just mean connection to some network.
		onLine: navigator.onLine,
		viewPortWidth: document &&
				window &&
				Math.max(document.documentElement.clientWidth, window.innerWidth || 0),

		viewPortHeight: document &&
				window &&
				Math.max(document.documentElement.clientHeight, window.innerHeight || 0)
	};

	packetToLog.visualisers = objectToLog.visualisers || {};

	// [P9052-12966] improve visualisers for error stats reports
	// - add source so we easily can identify our reports
	packetToLog.visualisers.source = 'cap';

	if (pastTimestamp) {
		// requests saved to storage in bootloader when logger was not available or
		// failed ajax request in logger.doAjaxRequest
		packetToLog.isRetry = true;
	}

	// If passed error is an instance of Error then send only the relevant data.
	// ErrorEvent and Event with error.type === 'error' should be send 
	// using custom error classes (AppError, ImageResourceError etc)
	if (objectToLog instanceof Error) { //instanceof Error = DOMException coming from FileReader

		const msg = objectToLog.message || objectToLog.name || 'message-not-defined';
		packetToLog.message = msg;
		packetToLog.errorId = objectToLog.errorId || ERRORS.UNKNOWN;
		objectToLog.tag && (packetToLog.tag = objectToLog.tag);

		packetToLog.error = {
			message: msg,
			name: objectToLog.name,
			// DOMException.code used for example with FileReader or localStorage exception
			code: objectToLog.code,
			stack: (objectToLog.stack || 'unavailable').toString(),
			file: objectToLog.fileName || 'unavailable',
			line: objectToLog.lineNumber || -1, // -1 by default so it's parsed as a number
			column: objectToLog.columnNumber || -1, // -1 by default so it's parsed as a number
		};

		if (objectToLog instanceof HttpError) {
			packetToLog.serverStatus = objectToLog.status;
			packetToLog.apiErrorId = objectToLog.getApiErrorId();
			if (packetToLog.apiErrorId) {
				packetToLog.apiErrorKey = errorsById[packetToLog.apiErrorId] || 'unknown';
			}
		}

		if (objectToLog instanceof BaseError) {
			const errorHistory = objectToLog.getErrorHistoryToLog();
			errorHistory && (packetToLog.errorHistory = errorHistory);

			const errorDetails = objectToLog.getPropertiesToLogAsJson();
			errorDetails && (packetToLog.errorDetails = errorDetails);

			const details = objectToLog.getPropertiesToLog();
			details && (packetToLog.details = details);

			const previousMessage = objectToLog.getMesageFromHistoryAtIndex(-1);
			if (previousMessage) {
				packetToLog.previousMessage = _.isString(previousMessage)
					?
					previousMessage
					:
					previousMessage.message || 'message-unknown';
				packetToLog.previousMessageId = previousMessage.errorId;
			}

			const firstMessage = objectToLog.getMesageFromHistoryAtIndex(0);
			if (firstMessage) {
				packetToLog.firstMessage = _.isString(firstMessage)
					?
					firstMessage
					: firstMessage.message || 'message-unknown';
				packetToLog.firstMessageId = firstMessage.errorId;

				if (firstMessage instanceof HttpError) {
					packetToLog.serverStatus = firstMessage.status;
					packetToLog.apiErrorId = firstMessage.getApiErrorId();
					if (packetToLog.apiErrorId) {
						packetToLog.apiErrorKey = errorsById[packetToLog.apiErrorId] || 'unknown';
					}
				}
			}

		} else {
			// from bootloader storage
			const propertiesToCopyFromSavedObject = [
				'errorHistory',
				'errorDetails',
				'details',
				'previousMessage',
				'previousMessageId',
				'firstMessage',
				'firstMessageId',
				'serverStatus',
				'apiErrorId'
			];

			for (let i = propertiesToCopyFromSavedObject.length - 1; i >= 0; i--) {
				const propertNameToCopy = propertiesToCopyFromSavedObject[i];
				if (objectToLog[propertNameToCopy]) {
					packetToLog[propertNameToCopy] = objectToLog[propertNameToCopy];
				}
			}
		}

		// [P9052-12966] improve visualisers for error stats reports
		// - add improved keys for report readability. Instead of using errorId we can stack by key, then message

		let key = errorsById[objectToLog.errorId];
		if (!key || key === errorsById[ERRORS.UNKNOWN]) {
			key = msg;
		}

		packetToLog.visualisers.key = key;
		!packetToLog.visualisers.feature && (packetToLog.visualisers.feature = 'GENERIC_FEATURE');

		if (window.hj) {
			const tags = ['ErrorKey', '' + packetToLog.errorId, _.snakeCase(key.replace(/[^a-z0-9-_ ]/gi, ''))];
			window.hj('tagRecording', tags);
		}

		// Add repeatedError and repeatedAlternatingError flags for kibana
		// Ignore "additional details" send from boot.js and REJECTED_API_STATUS_REQUEST as
		// they can break the chain of repeatedError
		if (!isRejectedAPiStatusRequest(packetToLog) && !isAdditionalDetailsMessage(packetToLog)) {
			const currentError = _.cloneDeep(_.omitBy(packetToLog.error, _.isNil));
			delete currentError.isSignedIn;
			// Compare by key and not stack, as stack can be different for same error,
			// for example same error on trailingEdge vs leadingEdge of function
			delete currentError.stack;
			currentError.key = key;

			const errorHistoryFromStorage = window.localStorage.getItem(ERROR_HISTORY_STORAGE_KEY);
			let errorHistoryItems = errorHistoryFromStorage ? JSON.parse(errorHistoryFromStorage) : [];
			const now = Date.now();

			if (!_.isEmpty(errorHistoryItems)) {
				// Delete outdated records
				for (let i = errorHistoryItems.length - 1; i >= 0; i--) {
					const historyError = errorHistoryItems[i];
					const timeDiff = now - historyError.timestamp;

					if (timeDiff > MAX_TIME_DIFF_BETWEEN_ERRORS) {
						delete errorHistoryItems[i];
					}
				}

				errorHistoryItems = errorHistoryItems.filter(function (item) { return item !== null; });

				const previousError = _.last(errorHistoryItems);
				if (previousError) {
					if (_.isEqual(previousError.errorData, currentError)) {
						packetToLog.error.repeatedError = true;
					} else if (errorHistoryItems.length >= 2) {

						for (let i = errorHistoryItems.length - 2 /* skip previousError */; i >= 0; i--) {
							const historyError = errorHistoryItems[i];

							if (_.isEqual(historyError.errorData, currentError)) {
								packetToLog.error.repeatedAlternatingError = true;
								break;
							}
						}

					}
				}
			}

			delete currentError.repeatedError;
			delete currentError.repeatedAlternatingError;

			const newErrorToPush = {
				errorData: currentError,
				timestamp: pastTimestamp || now,
				errorId: packetToLog.errorId
			};
			errorHistoryItems.push(newErrorToPush);

			try {
				window.localStorage.setItem(ERROR_HISTORY_STORAGE_KEY, JSON.stringify(errorHistoryItems));
			} catch (e) {
				// Silent catch
			}
		}
	}

	// clientId should be at the top level so it's consistent with server-side logs
	packetToLog.clientId = clientId;

	if (!userInfo.mail && lastAuthorizedUserEmail) {
		// if user info is not available yet but email is available from local storage
		packetToLog.user = {
			mail: lastAuthorizedUserEmail,
			name: 'pending'
		};
	} else {
		packetToLog.user = userInfo;
	}

	// don't log from dev machines when loggerConfig.sendFromDev is not set - buildNumber is available only after TC build
	if (REACT_APP_BUILD_NUMBER !== 'not available' || loggerConfig.sendFromDev) {
		sendViaHttp(parseMessage(packetToLog));
	}

	return packetToLog;
}

function saveErrorToLocalStorageQueue(data) {
	let errorQueue = getErrorQueue();
	// Clear queue in case of unexpected very large number of errors
	if (errorQueue.length > ERROR_QUEUE_MAX_RECORDS) {
		errorQueue = [];
	}

	const localStorageId = Date.now() + '-' + Math.random().toString(36).slice(2, 7);

	errorQueue.push({
		id: localStorageId,
		type: 'parsed',
		packet: data,
		timestamp: data.meta.timestamp,
		browserId: data.meta.browserId,
		browserTabId: data.meta.browserTabId,
		lastAuthorizedUserEmail: data.meta.lastAuthorizedUserEmail,
		buildNumber: REACT_APP_BUILD_NUMBER
	});

	const isSuccess = saveErrorQueue(errorQueue);
	return isSuccess ? localStorageId : null;
}

function sendViaHttp(data, localStorageItemId) {
	let resolvedLocalStorageItemId = localStorageItemId;
	// TODO:start temp flag used to send logs to new kibana index, should be removed in R116/7
	if (!data.miscellaneous) {
		data.miscellaneous = {};
	}
	// also update value in bootloader.js when you change this one
	data.miscellaneous.newlog1 = 'true';
	// TODO:end temp flag used to send logs to new kibana index, should be removed in R116/7

	// Save 'parsed' message coming from sendToServer to same localStorage key used to
	// save raw message from bootloader (raw message)
	// Only save errors to localStorage as they had to be retried when page is refreshed after error popup has been shown
	if (!resolvedLocalStorageItemId && data.error && !isRejectedAPiStatusRequest(data)) {
		resolvedLocalStorageItemId = saveErrorToLocalStorageQueue(data);
	}

	resolvedLocalStorageItemId && flagItemInErrorQueuBeingSentStatus(resolvedLocalStorageItemId, true);

	let requestNumber = 0;

	function onRetry(res) {
		setTimeout(function () {
			doAjaxRequest(requestNumber < LOGGER_AJAX_RETRY_MAX);
		}, LOGGER_AJAX_RETRY_TIMEOUT);
	}

	const doAjaxRequest = (shouldRetry) => {
		requestNumber++;
		if (requestNumber > 1) {
			data.isRetry = true;
		}

		axios.put(loggerConfig.endpoint, data)
			.then(() => {
				if (resolvedLocalStorageItemId) {
					// We remove retried and type=parsed items from localStorage once axios.then() is here triggered
					// and not earlier as user can hit refresh during retrying so log would be lost
					deleteItemFromErrorQueue(resolvedLocalStorageItemId);
				}
			})
			.catch((error) => {
				if (shouldRetry && requestNumber <= LOGGER_AJAX_RETRY_MAX) {
					onRetry();
				} else {
					resolvedLocalStorageItemId && flagItemInErrorQueuBeingSentStatus(resolvedLocalStorageItemId, false);
					//console.log('Logger: XHR failed');
				}
			});
	};	

	// Retry sending only errors (have errorId), ignore REJECTED_API_STATUS_REQUEST
	const shouldRetry = data.errorId && !isRejectedAPiStatusRequest(data);
	doAjaxRequest(shouldRetry);
}

function isRejectedAPiStatusRequest(obj) {
	return obj.errorId === ERRORS.REJECTED_API_STATUS_REQUEST ||
		obj.previousMessageId === ERRORS.REJECTED_API_STATUS_REQUEST;
}

function isAdditionalDetailsMessage(obj) {
	return obj && obj.message && obj.message.includes('additional details');
}

function isLocalStorageQuoateExceeded(e) {
	// In case of exception, try saving last error
	return e instanceof DOMException && (
		// everything except Firefox
		e.code === 22 ||
			// Firefox
			e.code === 1014 ||
			// test name field too, because code might not be present
			// everything except Firefox
			e.name === 'QuotaExceededError' ||
			// Firefox
			e.name === 'NS_ERROR_DOM_QUOTA_REACHED') &&
			// acknowledge QuotaExceededError only if there's something already stored
			(window.localStorage && window.localStorage.length !== 0);
}

function initBrowserDetails() {
	try {
		browserId = parseInt(window.localStorage.getItem('camonlineBrowserId'));
		if (!browserId) {
			browserId = Date.now();
			window.localStorage.setItem('camonlineBrowserId', browserId);
		}
	} catch (ex) {
		browserId = 'unavailable';
	}

	try {
		browserTabId = parseInt(window.sessionStorage.getItem('camonlineBrowserTabId'));
		if (!browserTabId) {
			browserTabId = Date.now();
			window.sessionStorage.setItem('camonlineBrowserTabId', browserTabId);
		}
	} catch (ex) {
		browserTabId = 'unavailable';
	}

	try {
		lastAuthorizedUserEmail = window.localStorage.getItem('camonlineLastAuthorizedUserEmail') || 'unavailable';
	} catch (ex) {
		lastAuthorizedUserEmail = 'unavailable';
	}
}

const componentsCache = {};
const API = {
	get: function (componentName) {
		
		const log = function () { };

		// if logger has been already created then return it
		if (componentsCache[componentName]) {
			return componentsCache[componentName];
		}

		/**
			*
			* @param {object | string} info If this is an error log, it MUST include errorId field as part of the object
			* @param {*} forceSilentMessageType Force use silent message type
		*/
		log.send = function (
			info,
			forceSilentMessageType /*includeItems*/,
			pastTimestamp,
			pastBrowserId,
			pastBrowserTabId,
			pastLastAuthorizedUserEmail) {

			info = sendToServer.call(
					this,
					info,
					forceSilentMessageType /*includeItems*/,
					pastTimestamp,
					pastBrowserId,
					pastBrowserTabId,
					pastLastAuthorizedUserEmail
			);
		};

		componentsCache[componentName] = log;

		return log;
	}
};

function getErrorQueue() {
	const errorQueueFromStorage = window.localStorage.getItem(ERROR_QUEUE_STORAGE_KEY);
	const errorQueue = !_.isEmpty(errorQueueFromStorage) ? JSON.parse(errorQueueFromStorage) : [];
	return errorQueue;
}

function saveErrorQueue(errorQueue) {
	try {
		window.localStorage.setItem(ERROR_QUEUE_STORAGE_KEY, JSON.stringify(errorQueue));
		return true;
	} catch (e) {
		if (isLocalStorageQuoateExceeded(e)) {
			errorQueue = [];
			window.localStorage.setItem(ERROR_QUEUE_STORAGE_KEY, JSON.stringify(errorQueue));
		}
		return false;
	}
}

function flagItemInErrorQueuBeingSentStatus(id, status) {
	const errorQueue = getErrorQueue();

	if (!_.isEmpty(errorQueue)) {
		for (let i = errorQueue.length - 1; i >= 0; i--) {
			if (id === 'all' || errorQueue[i].id === id) {
				errorQueue[i].isBeingSent = status;
			}
		}
		saveErrorQueue(errorQueue);
	}
}

function deleteItemFromErrorQueue(id) {
	let errorQueue = getErrorQueue();

	if (!_.isEmpty(errorQueue)) {
		for (let i = errorQueue.length - 1; i >= 0; i--) {
			if (errorQueue[i].id === id) {
				delete errorQueue[i];
			}
		}

		errorQueue = errorQueue.filter(function (item) { return item !== null; });
		saveErrorQueue(errorQueue);
	}
}

function sendLogsFromLocalStorageToELK() {
	const errorQueue = getErrorQueue();

	if (!_.isEmpty(errorQueue)) {
		const deletedItems = [];
		for (let i = errorQueue.length - 1; i >= 0; i--) {
			const pastError = errorQueue[i];

			if (!pastError.isBeingSent) {
				if (REACT_APP_BUILD_NUMBER === pastError.buildNumber) {
					pastError.packet.isRetry = true;
					sendViaHttp(pastError.packet, pastError.id);
				} else {
					// If buildNumber is different, delete item from localstorage as there could be incompatibility
					// between older item saved to localStorage and logger logic
					deletedItems.push(pastError.id);
				}
			}
		}

		// We remove type=raw items from localStorage here as these items will be saved as type=parsed
		// in localStorage again if ajax request fails
		deletedItems.forEach(function (id) {
			deleteItemFromErrorQueue(id);
		});
	}
}

export default API;

export const setupLogger = (userData, companyId, isPayingCompany) => {
	initUserInfo(userData, companyId, isPayingCompany);
	clearSavedIsBeingSentState();
	sendLogsFromLocalStorageToELK();
};


// WEBPACK FOOTER //
// ./src/logger/logger.js